import { omitUndefinedDeep } from "./omitUndefined";
import type {
  Design,
  FengShuiConsultation,
  FengShuiRanking,
  Furniture,
  Opening,
  Person,
  Room,
  Unit,
  WallMount,
} from "./types";

/**
 * Cloud-side type definitions for multiplayer-shared rooms.
 *
 * The local editor keeps the existing array-shaped {@link Design}; this
 * module defines the ID-keyed map shape that lives in Firestore so concurrent
 * mutations on different items merge cleanly via dotted-path `updateDoc`
 * (e.g. `update(ref, { ['furniture.f_abc.x']: 12 })`).
 *
 * Conversion happens at the sync boundary in `sharedRoomSync.ts`.
 */

export type ShareMemberRole = "owner" | "editor";

export interface ShareMember {
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: ShareMemberRole;
  joinedAt: number;
}

/**
 * Cloud-shape of a shared room. Mirrors {@link Design} except that
 * collections are ID-keyed maps (so two clients can mutate disjoint items
 * without trampling each other on a whole-array overwrite). Z-order lives
 * in `furnitureOrder` as an explicit array of ids.
 */
export interface SharedRoomDoc {
  id: string;
  ownerUid: string;
  ownerEmail: string;
  ownerDisplayName: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /** Membership map. Mutated only by the share Cloud Functions; rules
   *  pin equality with the prior value on every client write so a member
   *  can never widen membership themselves. */
  members: Record<string, ShareMember>;

  room: Room;
  furniture: Record<string, Furniture>;
  mounts: Record<string, WallMount>;
  openings: Record<string, Opening>;
  /** Ordered list of furniture ids; the canvas renders later ids on top. */
  furnitureOrder: string[];
  person: Person;
  unit: Unit;
  consultations: Record<string, FengShuiConsultation>;
  rankings: Record<string, FengShuiRanking>;
}

/**
 * Pointer doc written by `acceptShareInvite` so a member can list the
 * rooms shared with them without a collection-group query.
 *
 * Path: `users/{uid}/sharedInbox/{roomId}`.
 */
export interface SharedInboxEntry {
  roomId: string;
  ownerUid: string;
  ownerDisplayName: string;
  ownerEmail: string;
  name: string;
  joinedAt: number;
  lastOpenedAt: number;
}

export type ShareInviteStatus = "pending" | "accepted" | "revoked";

export interface ShareInvite {
  id: string;
  roomId: string;
  roomName: string;
  ownerUid: string;
  ownerDisplayName: string;
  ownerEmail: string;
  /**
   * `email` invites are locked to one address; `link` invites admit
   * anyone with the URL (after Google sign-in) up to `maxUses` times
   * before `expiresAt`. Docs written before link invites existed carry
   * no `kind` and are email invites.
   */
  kind?: ShareInviteKind;
  /** Lowercased on write; matched case-insensitively against the verified
   *  Firebase Auth token email of the accepting user. `null` for link
   *  invites. */
  granteeEmail: string | null;
  status: ShareInviteStatus;
  createdAt: number;
  acceptedAt?: number;
  acceptedByUid?: string;
  /** Link invites only. */
  maxUses?: number;
  uses?: number;
  expiresAt?: number | null;
}

export type ShareInviteKind = "email" | "link";

export function shareInviteKind(invite: Pick<ShareInvite, "kind">): ShareInviteKind {
  return invite.kind === "link" ? "link" : "email";
}

// ---------------------------------------------------------------------------
// Serializers — convert between local array-shape `Design` and cloud
// map-shape `SharedRoomDoc.{furniture,mounts,openings,...}` fields.
// ---------------------------------------------------------------------------

/**
 * Convert a local array-shape {@link Design} into the map-shape collections
 * a {@link SharedRoomDoc} expects. Z-order is preserved in `furnitureOrder`.
 *
 * Used at room-creation time and as a sanity reference for diff helpers.
 */
export function designToCloudCollections(design: Design): {
  room: Room;
  furniture: Record<string, Furniture>;
  mounts: Record<string, WallMount>;
  openings: Record<string, Opening>;
  furnitureOrder: string[];
  person: Person;
  unit: Unit;
  consultations: Record<string, FengShuiConsultation>;
  rankings: Record<string, FengShuiRanking>;
} {
  const furniture: Record<string, Furniture> = {};
  for (const f of design.furniture) furniture[f.id] = f;
  const mounts: Record<string, WallMount> = {};
  for (const m of design.mounts) mounts[m.id] = m;
  const openings: Record<string, Opening> = {};
  for (const o of design.openings) openings[o.id] = o;
  const consultations: Record<string, FengShuiConsultation> = {};
  for (const c of design.consultations ?? []) consultations[c.id] = c;
  const rankings: Record<string, FengShuiRanking> = {};
  for (const r of design.rankings ?? []) rankings[r.id] = r;

  return {
    room: design.room,
    furniture,
    mounts,
    openings,
    furnitureOrder: design.furniture.map((f) => f.id),
    person: design.person,
    unit: design.unit,
    consultations,
    rankings,
  };
}

/**
 * Reconstruct a local array-shape {@link Design} from the cloud doc.
 *
 * `furnitureOrder` defines render order. Items present in the map but
 * absent from the order array are appended at the end (defensive — should
 * never happen if writers stay consistent). Items in the order array but
 * missing from the map are silently skipped.
 */
export function cloudToDesign(doc: SharedRoomDoc): Design {
  const orderedIds = doc.furnitureOrder ?? [];
  const seenIds = new Set<string>();
  const furniture: Furniture[] = [];
  for (const id of orderedIds) {
    const f = doc.furniture?.[id];
    if (!f) continue;
    furniture.push(f);
    seenIds.add(id);
  }
  for (const [id, f] of Object.entries(doc.furniture ?? {})) {
    if (seenIds.has(id)) continue;
    furniture.push(f);
  }

  const mounts = Object.values(doc.mounts ?? {});
  const openings = Object.values(doc.openings ?? {});
  const consultations = Object.values(doc.consultations ?? {}).sort(
    (a, b) => a.at - b.at
  );
  const rankings = Object.values(doc.rankings ?? {}).sort((a, b) => a.at - b.at);

  return {
    room: doc.room,
    furniture,
    mounts,
    openings,
    person: doc.person,
    unit: doc.unit,
    consultations,
    rankings,
  };
}

// ---------------------------------------------------------------------------
// Diffing — the outbound-sync orchestrator computes a granular diff between
// the previous and the new local design, and converts the diff into a single
// dotted-path `updateDoc` payload.
//
// We keep this pure (no Firestore types here) so it's trivially unit
// testable and the sync orchestrator stays a thin wrapper.
// ---------------------------------------------------------------------------

/**
 * Sentinel that the sync orchestrator translates into Firestore's
 * `deleteField()` at write time. Keeping serializers Firestore-free means
 * this module stays trivially testable.
 */
export const DELETE_FIELD = Symbol("DELETE_FIELD");

export type CloudDiffValue =
  | string
  | number
  | boolean
  | null
  | typeof DELETE_FIELD
  | Record<string, unknown>
  | unknown[];

/**
 * Compute a Firestore-friendly diff between two local `Design`s.
 *
 * Returns a flat record of dotted-path fields. The keys point at leaf or
 * sub-object positions inside the cloud doc shape; the values are the new
 * cloud value (or {@link DELETE_FIELD} sentinel for removals).
 *
 * Strategy:
 *   - Per item, if the old version is missing → write the whole item.
 *   - If the new version is missing → mark for deletion.
 *   - If both exist → compare property-by-property and emit a dotted-path
 *     update only for the properties that actually changed.
 *
 * This is intentionally NOT a CRDT — we ride Firestore's per-field LWW (the
 * Figma model). Two clients moving the same item land on whichever write
 * arrived last; two clients moving different items merge cleanly.
 *
 * The `selfDraggingId` parameter tells the orchestrator to suppress writes
 * for an item the local user is currently dragging — those positions go
 * through RTDB during the drag, not Firestore. The final position commits
 * once on drag-end.
 */
export interface DiffOptions {
  /** Furniture id currently being dragged locally. While set, position
   *  changes for that piece are suppressed (handled via RTDB instead). */
  selfDraggingId?: string | null;
  /** Mount id currently being dragged locally. */
  selfDraggingMountId?: string | null;
  /** Opening id currently being dragged locally. */
  selfDraggingOpeningId?: string | null;
}

export function diffDesignToCloudUpdates(
  prev: Design,
  next: Design,
  opts: DiffOptions = {}
): Record<string, CloudDiffValue> {
  const updates: Record<string, CloudDiffValue> = {};

  diffMap(
    "furniture",
    keyById(prev.furniture),
    keyById(next.furniture),
    updates,
    opts.selfDraggingId ?? null,
    POSITION_KEYS_FURNITURE
  );
  diffMap(
    "mounts",
    keyById(prev.mounts),
    keyById(next.mounts),
    updates,
    opts.selfDraggingMountId ?? null,
    POSITION_KEYS_MOUNT
  );
  diffMap(
    "openings",
    keyById(prev.openings),
    keyById(next.openings),
    updates,
    opts.selfDraggingOpeningId ?? null,
    POSITION_KEYS_OPENING
  );

  // Furniture order: rewrite the entire array if it changed. Reorders
  // are rare and intentional; the LWW semantics on a 1-D array are
  // acceptable given they don't affect geometry.
  const prevOrder = prev.furniture.map((f) => f.id);
  const nextOrder = next.furniture.map((f) => f.id);
  if (!arraysEqual(prevOrder, nextOrder)) {
    updates.furnitureOrder = nextOrder;
  }

  if (!shallowEqual(prev.room, next.room)) {
    updates.room = omitUndefinedDeep(next.room) as unknown as Record<string, unknown>;
  }
  if (!shallowEqual(prev.person, next.person)) {
    updates.person = omitUndefinedDeep(next.person) as unknown as Record<string, unknown>;
  }
  if (prev.unit !== next.unit) {
    updates.unit = next.unit;
  }

  // Append-only logs (consultations, rankings) — emit per-id additions
  // and removals as map entries. Lets two collaborators concurrently
  // append without races.
  const prevConsultations = keyById(prev.consultations ?? []);
  const nextConsultations = keyById(next.consultations ?? []);
  for (const id of Object.keys(nextConsultations)) {
    if (!prevConsultations[id]) {
      updates[`consultations.${id}`] = nextConsultations[id] as unknown as Record<
        string,
        unknown
      >;
    }
  }
  for (const id of Object.keys(prevConsultations)) {
    if (!nextConsultations[id]) {
      updates[`consultations.${id}`] = DELETE_FIELD;
    }
  }
  const prevRankings = keyById(prev.rankings ?? []);
  const nextRankings = keyById(next.rankings ?? []);
  for (const id of Object.keys(nextRankings)) {
    if (!prevRankings[id]) {
      updates[`rankings.${id}`] = nextRankings[id] as unknown as Record<
        string,
        unknown
      >;
    }
  }
  for (const id of Object.keys(prevRankings)) {
    if (!nextRankings[id]) {
      updates[`rankings.${id}`] = DELETE_FIELD;
    }
  }

  return updates;
}

const POSITION_KEYS_FURNITURE: ReadonlySet<string> = new Set([
  "x",
  "y",
  "rotation",
]);
const POSITION_KEYS_MOUNT: ReadonlySet<string> = new Set([
  "wallIndex",
  "alongWall",
  "fromFloor",
]);
const POSITION_KEYS_OPENING: ReadonlySet<string> = new Set([
  "wallIndex",
  "alongWall",
  "fromFloor",
]);

function diffMap<T extends { id: string }>(
  fieldName: string,
  prev: Record<string, T>,
  next: Record<string, T>,
  updates: Record<string, CloudDiffValue>,
  draggingId: string | null,
  positionKeys: ReadonlySet<string>
): void {
  for (const id of Object.keys(next)) {
    const a = prev[id];
    const b = next[id];
    if (!a) {
      // Brand-new item. If it's the one being dragged locally, skip the
      // whole add — the drag preview already streams via RTDB and we'll
      // commit a single setField on drag-end.
      if (id === draggingId) continue;
      updates[`${fieldName}.${id}`] = omitUndefinedDeep(b) as unknown as Record<
        string,
        unknown
      >;
      continue;
    }
    // Property-level diff. The `isDragging` arg tells `diffItem` to omit
    // position-keys for the dragged item; non-position fields (name,
    // dimensions, color, etc.) still flow through normally.
    diffItem(fieldName, id, a, b, updates, id === draggingId, positionKeys);
  }
  for (const id of Object.keys(prev)) {
    if (next[id]) continue;
    if (id === draggingId) continue;
    updates[`${fieldName}.${id}`] = DELETE_FIELD;
  }
}

function diffItem<T extends { id: string }>(
  fieldName: string,
  id: string,
  a: T,
  b: T,
  updates: Record<string, CloudDiffValue>,
  isDragging: boolean,
  positionKeys: ReadonlySet<string>
): void {
  const aRec = a as unknown as Record<string, unknown>;
  const bRec = b as unknown as Record<string, unknown>;
  const allKeys = new Set([...Object.keys(aRec), ...Object.keys(bRec)]);
  for (const key of allKeys) {
    if (isDragging && positionKeys.has(key)) continue;
    const av = aRec[key];
    const bv = bRec[key];
    if (deepEqual(av, bv)) continue;
    if (bv === undefined) {
      updates[`${fieldName}.${id}.${key}`] = DELETE_FIELD;
    } else {
      updates[`${fieldName}.${id}.${key}`] = bv as CloudDiffValue;
    }
  }
}

function keyById<T extends { id: string }>(
  list: T[] | undefined
): Record<string, T> {
  const out: Record<string, T> = {};
  if (!list) return out;
  for (const item of list) out[item.id] = item;
  return out;
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function shallowEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return a === b;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---------------------------------------------------------------------------
// RTDB presence types — written under `presence/{roomId}/{uid}/{tabId}`.
// Each tab gets its own entry so multi-tab users don't clobber each other;
// the presence consumer aggregates per-uid client-side.
// ---------------------------------------------------------------------------

export type PresenceCanvasView = "design" | "wall" | "build";

export interface PresenceCursor {
  view: PresenceCanvasView;
  /** Wall index when view === "wall"; otherwise null. Null for interior walls. */
  wallIndex: number | null;
  /** Set when the cursor is on an interior wall elevation. */
  interiorWallId?: string | null;
  interiorSegment?: number | null;
  /** Cursor coordinate in room inches (room space, not pixel). Stable
   *  across zoom/pan. For the wall view, x/y is along-wall x from-floor. */
  x: number;
  y: number;
}

/** A user's current selection. Mirrors the same `{kind: id}` shape the
 *  store carries; only the populated field is non-null. */
export interface PresenceSelection {
  furnitureId: string | null;
  mountId: string | null;
  openingId: string | null;
  roomWallIndex: number | null;
  roomVertexIndex: number | null;
}

/**
 * Mid-drag preview broadcast. While a user is dragging, their final
 * position isn't committed to Firestore yet — collaborators still want to
 * see the live ghost at 30 Hz. The receiver renders the item at this
 * payload's coords, ignoring whatever Firestore says for that item.
 */
export type PresenceDragging =
  | {
      kind: "furniture";
      id: string;
      x: number;
      y: number;
      rotation: number;
    }
  | {
      kind: "mount";
      id: string;
      wallIndex: number;
      alongWall: number;
      fromFloor: number;
      interiorWallId?: string | null;
      interiorSegment?: number | null;
    }
  | {
      kind: "opening";
      id: string;
      wallIndex: number;
      alongWall: number;
      fromFloor: number;
      interiorWallId?: string | null;
      interiorSegment?: number | null;
    }
  | {
      kind: "roomVertex";
      index: number;
      x: number;
      y: number;
    };

export interface PresenceEntry {
  uid: string;
  tabId: string;
  displayName: string;
  photoURL: string | null;
  /** Hex `#RRGGBB`, deterministic from uid. */
  color: string;
  cursor: PresenceCursor | null;
  selection: PresenceSelection | null;
  dragging: PresenceDragging | null;
  /** RTDB serverTimestamp on writes; epoch ms when read back. */
  lastSeen: number;
}

/**
 * Aggregated per-user presence — the public shape the UI consumes. When a
 * user has multiple tabs open we pick the one with the freshest
 * `lastSeen`; cursor/selection from older tabs is hidden so we don't
 * show two cursors per person.
 */
export interface CollaboratorState {
  uid: string;
  displayName: string;
  photoURL: string | null;
  color: string;
  cursor: PresenceCursor | null;
  selection: PresenceSelection | null;
  dragging: PresenceDragging | null;
  lastSeen: number;
}
